"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.constructNode = constructNode;
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const featureFlags_1 = require("../../common/featureFlags");
const localizeUtils_1 = require("../../common/localizeUtils");
const commonNodes_1 = require("./commonNodes");
const CapabilityOptions_1 = require("./vsc/CapabilityOptions");
const create_1 = require("../create");
const questionNames_1 = require("../questionNames");
function isFeatureEnabled(flagName) {
    return featureFlags_1.featureFlagManager.getBooleanValue({ name: flagName, defaultValue: "false" });
}
function constructNode(jsonContent, platform = teamsfx_api_1.Platform.VSCode) {
    const jsonObject = JSON.parse(jsonContent);
    // Handle named node references (TypeScript-defined sub-trees)
    if (jsonObject.node) {
        return resolveNodeReference(jsonObject, platform);
    }
    // Handle "group" type (no options)
    if (jsonObject.data.type === "group") {
        const node = {
            data: { type: "group", name: jsonObject.data.name },
        };
        if (jsonObject.condition)
            node.condition = jsonObject.condition;
        if (jsonObject.children) {
            node.children = [];
            for (const child of jsonObject.children) {
                node.children.push(constructNode(JSON.stringify(child), platform));
            }
        }
        return node;
    }
    const data = {
        type: "singleSelect",
        name: jsonObject.data.name,
        title: jsonObject.data.title ? (0, localizeUtils_1.getLocalizedString)(jsonObject.data.title) : "",
        placeholder: jsonObject.data.placeholder
            ? (0, localizeUtils_1.getLocalizedString)(jsonObject.data.placeholder)
            : undefined,
        skipSingleOption: jsonObject.data.skipSingleOption,
        forgetLastValue: jsonObject.data.forgetLastValue,
        staticOptions: [],
        onDidSelection: jsonObject.data.name === questionNames_1.QuestionNames.WithPlugin ? CapabilityOptions_1.setTemplateNameAndGC : CapabilityOptions_1.setTemplateName,
    };
    if (jsonObject.data.options) {
        for (const option of jsonObject.data.options) {
            if (option.featureFlag && !isFeatureEnabled(option.featureFlag)) {
                continue;
            }
            const optionItem = {
                id: option.id,
                label: option.icon && platform === teamsfx_api_1.Platform.VSCode
                    ? `${String(option.icon)} ${(0, localizeUtils_1.getLocalizedString)(option.label)}`
                    : (0, localizeUtils_1.getLocalizedString)(option.label),
                detail: option.detail ? (0, localizeUtils_1.getLocalizedString)(option.detail) : undefined,
                data: option.data,
            };
            if (option.groupName) {
                optionItem.groupName = (0, localizeUtils_1.getLocalizedString)(option.groupName);
            }
            data.staticOptions.push(optionItem);
        }
    }
    const node = { data };
    if (jsonObject.condition) {
        node.condition = jsonObject.condition;
    }
    if (jsonObject.children) {
        node.children = [];
        for (const child of jsonObject.children) {
            node.children.push(constructNode(JSON.stringify(child), platform));
        }
    }
    return node;
}
function resolveNodeReference(jsonObject, platform) {
    let node;
    switch (jsonObject.node) {
        // Common shared nodes (from commonNodes.ts)
        case "llmServiceNode":
            return (0, commonNodes_1.llmServiceNode)(jsonObject.condition);
        case "apiSpecNode":
            return (0, commonNodes_1.apiSpecNode)(jsonObject.condition);
        case "apiSpecWithSearchNode": {
            const n = (0, commonNodes_1.apiSpecWithSearchNode)();
            if (jsonObject.condition)
                n.condition = jsonObject.condition;
            return n;
        }
        case "azureOpenAINode":
            return (0, commonNodes_1.azureOpenAINode)(jsonObject.condition);
        case "foundryNode":
            return (0, commonNodes_1.foundryNode)(jsonObject.condition);
        // TypeScript-defined complex nodes (lazy import to avoid circular dependency)
        case "mcpServerTypeNode": {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const { MCPServerTypeNode } = require("./vsc/teamsProjectTypeNode");
            node = MCPServerTypeNode();
            break;
        }
        // Simple question nodes
        case "gcNameNode":
            node = { data: (0, create_1.GCNameQuestion)() };
            break;
        case "gcConnectionIdNode":
            node = { data: (0, create_1.GCConnectionIdQuestion)() };
            break;
        case "officeAddinFolderNode":
            node = {
                data: {
                    type: "folder",
                    name: questionNames_1.QuestionNames.OfficeAddinFolder,
                    title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.DAMetaOS.capability.upgradeProject.projectFolder.title"),
                    placeholder: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.DAMetaOS.capability.upgradeProject.projectFolder.placeholder"),
                },
            };
            break;
        case "officeAddinImportNode":
            node = {
                data: { type: "group", name: questionNames_1.QuestionNames.OfficeAddinImport },
                children: [
                    {
                        data: {
                            type: "folder",
                            name: questionNames_1.QuestionNames.OfficeAddinFolder,
                            title: "Existing add-in project folder",
                        },
                    },
                    {
                        data: {
                            type: "singleFile",
                            name: questionNames_1.QuestionNames.OfficeAddinManifest,
                            title: "Select import project manifest file",
                        },
                    },
                ],
            };
            break;
        default:
            throw new Error(`Unknown node reference: ${jsonObject.node}`);
    }
    if (jsonObject.condition) {
        node.condition = jsonObject.condition;
    }
    return node;
}
//# sourceMappingURL=constructNode.js.map